import Member from 'iam/models/Member'
import { CancellationToken } from 'infra/cancellation'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { Observable, Subject, combineLatest, firstValueFrom } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  switchMap,
  takeUntil,
} from 'rxjs/operators'
import { CursorPageQuery, CursorPaged, Id } from 'types/definitions'
import { fromStream, toStream } from 'utils/mobx-util'
import { shareLatest } from 'utils/rx-util'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { OwnerSelected } from './DraftChange'
import { DraftStateSlice, PrepareContext } from './DraftStateSlice'
import { StepFactory, StepViewModel } from './OutlineViewModel'
import { IamPort } from './ports'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { OwnerScopedInput } from './PrefilledDraftInput'
import { UserDataType } from 'iam/models/UserDataType'

const TITLE = 'Who would you like to submit this job for?'

/**
 * Owner Selector view model, for selecting the job owner
 */
export default class OwnerSelectorViewModel extends DraftStateSlice {
  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Changes subject.
   * @private
   */
  private readonly changesSubject = new Subject<OwnerSelected>()

  /**
   * Stream for whether the step is valid.
   */
  isValid$ = toStream(() => this.isValid, true)

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Changes stream.
   */
  readonly changes$ = this.changesSubject.asObservable()

  /**
   * The selected owner.
   */
  @observable _selectedOwner: Member | null = null

  /**
   * Scoped to owner inputs.
   */
  @observable ownerScopedInput?: OwnerScopedInput

  /**
   * Emits the selected owner on subscribe, but only after `prepare` has been run.
   */
  readonly selectedOwner$ = toStream(() => this.selectedOwner, true).pipe(
    distinctUntilChanged(),
    shareLatest(),
    // Don't start emitting until the slice is ready.
    this.whileReady(),
  )

  /**
   * Streams as MobX Observables.
   */
  private readonly $ = {
    selectedWorkspaceMember: fromStream(null, this.selectedWorkspaceMember$),
  }

  /**
   * Constructor.
   *
   * @param isActive$ Observable for whether the slice is active.
   * @param createStep Factory for creating a Step.
   * @param selectedWorkspaceMember$ Observable for the selected workspace member
   * @param adapter The IAM adapter.
   * @param _isDisabled Getter for whether the selector is disabled
   * @param _canEditDraftJobsAsAdmin Getter for whether the selector is visible
   */
  constructor(
    isActive$: Observable<boolean>,
    createStep: StepFactory,
    private selectedWorkspaceMember$: Observable<Member | null>,
    private adapter: IamPort,
    private _isDisabled: () => boolean,
    private _canEditDraftJobsAsAdmin: () => boolean,
  ) {
    super(isActive$)
    makeObservable(this)
    this.selectedOwner$.subscribe()
    this.changes$.subscribe()

    combineLatest([
      toStream(() => this.scrollProvider, true),
      toStream(() => this.selectedOwner, true),
    ])
      .pipe(
        filter(([, owner]) => owner === null),
        switchMap(([scrollProvider]) => scrollProvider.fetch()),
        this.whileReady(),
      )
      .subscribe()

    this.searchText.debouncedValue$
      .pipe(this.whileReady())
      .subscribe(() => this.scrollProvider.fetch())

    this.step = createStep({
      stepId: 'owner_selection',
      isValid: () => Boolean(this._selectedOwner),
      outlineItemDisplay: () => {
        if (!this.selectedOwner) {
          return { title: TITLE }
        }

        return {
          title: TITLE,
          value: this.selectedOwner.displayName,
          visual: {
            type: 'Image',
            url: this.selectedOwner.avatar,
          },
        }
      },
    })
  }

  /**
   * Whether the step is valid.
   */
  @computed
  get isValid() {
    return Boolean(this.selectedOwner)
  }

  /**
   * This is the only step container.
   */
  @computed
  get stepContainers() {
    return this._canEditDraftJobsAsAdmin() ? [this] : []
  }

  /**
   * Whether the owner selector is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled() || Boolean(this.ownerScopedInput)
  }

  /**
   * The scroll provider for Members.
   */
  @computed
  get scrollProvider() {
    const member = this.$.selectedWorkspaceMember()
    return new InfiniteScrollProvider<Member>({
      limit: 20,
      fetchItems: async (params) => this.fetchMembers(member, params),
      startFetchTaskAsPending: true,
    })
  }

  /**
   * The currently selected owner
   */
  @computed
  get selectedOwner() {
    return this._selectedOwner
  }

  /**
   * Hydrates the Owner Selector state from the Job.
   *
   * @param context
   * @param ct
   */
  async prepare(context: PrepareContext, ct = CancellationToken.default) {
    // Wait for the WS selector to have been prepared.
    await firstValueFrom(
      this.selectedWorkspaceMember$.pipe(takeUntil(ct.asObservable())),
      { defaultValue: null },
    )

    ct.throwIfCancelled()

    if (context.type === 'EditDraft') {
      this.setOwner(context.job.owner)
      return
    }

    // Must clear or set any owner scoped inputs before further preparation
    this.setOwnerScopeInput(context.prefilledDraft.ownerScopedInput)

    const prefilledUserPublicId =
      context.prefilledDraft.userPublicId ||
      context.prefilledDraft.ownerScopedInput?.publicId

    if (!prefilledUserPublicId) {
      this.selectOwner(this.$.selectedWorkspaceMember())
      return
    }

    this.selectOwner(
      await this.adapter.fetchMember(
        this.$.selectedWorkspaceMember()?.workspace.id,
        prefilledUserPublicId,
      ),
    )
  }

  /**
   * Sets whether component should be scoped to job owner inputs.
   *
   * @param scopedInput
   */
  @action
  setOwnerScopeInput(scopedInput?: OwnerScopedInput) {
    this.ownerScopedInput = scopedInput
  }

  /**
   * Sets the selected owner.
   *
   * @param owner
   */
  @action.bound
  setOwner(owner: Member | null) {
    this._selectedOwner = owner
  }

  /**
   * Selects the specified owner.
   *
   * @param owner
   */
  @action.bound
  selectOwner(owner: Member | undefined | null) {
    owner ??= null

    if (owner !== this._selectedOwner) {
      this.setOwner(owner)
      this.changesSubject.next({ owner, type: 'OwnerSelected' })
    }
  }

  /**
   * Triggers new member search.
   *
   * @param newText
   */
  @action.bound
  triggerSearch(newText: string) {
    this.searchText.change(newText)
  }

  /**
   * Fetches the workspace's members.
   */
  fetchMembers = task.resolved(
    async (member: Member | null, params: CursorPageQuery) => {
      if (!member) {
        return CursorPaged.empty
      }

      if (
        !member.hasPermission('ADMIN_USER_LIST') &&
        !member.hasPermission('ADMIN_CLIENT_USER_LIST')
      ) {
        return CursorPaged.fromArray([member])
      }

      return this.adapter.fetchAvailableMembersForWorkspace({
        ...params,
        user_types:
          !member.hasPermission('ADMIN_USER_LIST') &&
          member.hasPermission('ADMIN_CLIENT_USER_LIST')
            ? [UserDataType.User]
            : undefined,
        workspace_id: Id.assert(member.workspace.id),
        search_text: this.searchText.value,
      })
    },
  )
}
